import { useState, useEffect, useMemo } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { firestore } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { RepairRequest } from '../types/firestore';
import { isApprovedServiceAdmin } from '../utils/accessControl';
import {
  getRepairRequestId,
  getRepairRequestUnreadState,
  isRepairRequestAssignedToServiceUser,
  REPAIR_REQUEST_ASSIGNMENT_FIELDS,
  REPAIR_REQUEST_SERVICE_UNREAD_LEGACY_TYPES,
  REPAIR_REQUEST_UNREAD_FIELDS,
  REPAIR_REQUEST_UNREAD_SECTIONS,
} from '../utils/repairRequestReadModel';

export function useUnreadServiceActivities() {
  const { currentUser, userProfile, claims } = useAuth();
  const [sectionRequests, setSectionRequests] = useState<RepairRequest[]>([]);
  const [legacyRequests, setLegacyRequests] = useState<RepairRequest[]>([]);
  const [loadedSectionCanonical, setLoadedSectionCanonical] = useState(false);
  const [loadedLegacyCanonical, setLoadedLegacyCanonical] = useState(false);

  // Service notifications are only for approved service admins (role + active status).
  const isService = isApprovedServiceAdmin(userProfile, claims);

  const unreadRequests = useMemo(() => {
    const byId = new Map<string, RepairRequest>();
    sectionRequests.forEach((req) => {
      const key = getRepairRequestId(req);
      if (key) {
        byId.set(key, req);
      }
    });
    legacyRequests.forEach((req) => {
      const key = getRepairRequestId(req);
      if (key && !byId.has(key)) {
        byId.set(key, req);
      }
    });
    const data = Array.from(byId.values()).filter(
      (req) =>
        isRepairRequestAssignedToServiceUser(req, currentUser?.uid) &&
        getRepairRequestUnreadState(req, 'service').sections.length > 0
    );
    data.sort((a, b) => {
      const timeA = a.lastServiceActivityAt?.toDate?.().getTime?.() || 0;
      const timeB = b.lastServiceActivityAt?.toDate?.().getTime?.() || 0;
      return timeB - timeA;
    });
    return data;
  }, [currentUser?.uid, sectionRequests, legacyRequests]);

  const loading = !loadedSectionCanonical || !loadedLegacyCanonical;

  useEffect(() => {
    if (!currentUser || !isService) {
      setSectionRequests([]);
      setLegacyRequests([]);
      setLoadedSectionCanonical(true);
      setLoadedLegacyCanonical(true);
      return;
    }

    const baseCollection = collection(firestore, 'repairRequests');
    const assignmentFields = REPAIR_REQUEST_ASSIGNMENT_FIELDS;
    const unreadFields = REPAIR_REQUEST_UNREAD_FIELDS.service;

    setSectionRequests([]);
    setLegacyRequests([]);
    setLoadedSectionCanonical(false);
    setLoadedLegacyCanonical(false);

    const canonicalSectionsQuery = query(
      baseCollection,
      where(assignmentFields.canonicalServiceProviderId, '==', currentUser.uid),
      where(unreadFields.sectionsField, 'array-contains-any', REPAIR_REQUEST_UNREAD_SECTIONS)
    );
    const canonicalLegacyQuery = query(
      baseCollection,
      where(assignmentFields.canonicalServiceProviderId, '==', currentUser.uid),
      where(unreadFields.legacyTypeField, 'in', REPAIR_REQUEST_SERVICE_UNREAD_LEGACY_TYPES)
    );

    let sectionsCanonicalData: RepairRequest[] = [];
    let legacyCanonicalData: RepairRequest[] = [];

    const combineUnique = (items: RepairRequest[]) => {
      const uniqueMap = new Map<string, RepairRequest>();
      items.forEach((item) => {
        const id = getRepairRequestId(item);
        if (id) {
          uniqueMap.set(id, item);
        }
      });
      return Array.from(uniqueMap.values());
    };

    const updateSections = () => {
      setSectionRequests(combineUnique(sectionsCanonicalData));
    };

    const updateLegacy = () => {
      setLegacyRequests(combineUnique(legacyCanonicalData));
    };

    const unsubSectionCanonical = onSnapshot(
      canonicalSectionsQuery,
      (snapshot) => {
        sectionsCanonicalData = snapshot.docs.map(
          (doc) => ({ uid: doc.id, id: doc.id, ...doc.data() }) as RepairRequest
        );
        setLoadedSectionCanonical(true);
        updateSections();
      },
      (err) => {
        console.error('Canonical service unread sections snapshot error', err);
        setLoadedSectionCanonical(true);
      }
    );

    const unsubLegacyCanonical = onSnapshot(
      canonicalLegacyQuery,
      (snapshot) => {
        legacyCanonicalData = snapshot.docs
          .map((doc) => ({ uid: doc.id, id: doc.id, ...doc.data() }) as RepairRequest)
          .filter((req) => !getRepairRequestUnreadState(req, 'service').hasCanonicalSections);
        setLoadedLegacyCanonical(true);
        updateLegacy();
      },
      (err) => {
        console.error('Canonical service unread legacy snapshot error', err);
        setLoadedLegacyCanonical(true);
      }
    );

    return () => {
      unsubSectionCanonical();
      unsubLegacyCanonical();
    };
  }, [currentUser, isService]);

  return { unreadRequests, loading };
}
